/**
 * XO Fetti — Collection Filter Bar
 * Handles filter dropdown interactions, URL-based tag filtering, and price filtering.
 *
 * Tag convention:
 *   shopfor-{value}   → Shop For filter
 *   category-{value}  → Category filter
 *   color-{value}     → Color filter
 *   theme-{value}     → Theme filter
 *   Price is filtered client-side by product data-price attribute.
 */
(function () {
  'use strict';

  // ── State ──
  var selectedFilters = {}; // { 'shopfor': 'shopfor-bride', 'category': 'category-apparel', ... }
  var selectedPrice = null; // { min: 10, max: 20 }
  var pendingSelections = {}; // Temp state while dropdown is open
  var wholesale = false;

  // ── Config ──
  var PREFIXES = ['shopfor', 'category', 'color', 'theme'];

  var PRICE_RANGES = [
    { label: 'Under $10', min: 0, max: 10 },
    { label: '$10 \u2013 $20', min: 10, max: 20 },
    { label: '$20 \u2013 $35', min: 20, max: 35 },
    { label: '$35+', min: 35, max: 999999 }
  ];

  // ── Init ──
  function init() {
    // Read current tags from URL to set initial state
    var currentTagsEl = document.getElementById('xo-filter-config');
    if (!currentTagsEl) return;

    var config;
    try {
      config = JSON.parse(currentTagsEl.textContent);
    } catch (e) {
      return;
    }

    wholesale = config.wholesale === true;
    if (wholesale) {
      PRICE_RANGES = [
        { label: 'Under $5', min: 0, max: 5 },
        { label: '$5 \u2013 $10', min: 5, max: 10 },
        { label: '$10 \u2013 $20', min: 10, max: 20 },
        { label: '$20+', min: 20, max: 999999 }
      ];
    }

    // Parse current_tags into selectedFilters
    if (config.currentTags && config.currentTags.length) {
      config.currentTags.forEach(function (tag) {
        var tagHandle = slugify(tag);
        PREFIXES.forEach(function (prefix) {
          if (tagHandle.indexOf(prefix + '-') === 0) {
            selectedFilters[prefix] = tagHandle;
          }
        });
      });
    }

    // Parse price from URL params
    var urlParams = new URLSearchParams(window.location.search);
    var priceParam = urlParams.get('xo_price');
    if (priceParam) {
      var parts = priceParam.split('-');
      if (parts.length === 2) {
        selectedPrice = { min: parseFloat(parts[0]), max: parseFloat(parts[1]) };
        if (wholesale && !PRICE_RANGES.some(function (range) {
          return range.min === selectedPrice.min && range.max === selectedPrice.max;
        })) selectedPrice = null;
      }
    }

    // Render active pills
    renderActivePills();

    // Apply client-side price filter if active
    if (selectedPrice) {
      applyPriceFilter();
      updateProductCount();
    }

    // Bind events
    bindEvents();
    if (wholesale && selectedPrice) {
      preserveCostPagination();
      // Collection infinite scrolling can append new cards after initial filtering.
      new MutationObserver(function (records) {
        if (records.some(function (record) { return Array.from(record.addedNodes).some(function (node) {
          return node.nodeType === 1 && (node.matches('.product-grid-item-buy, a.pagination__item, a.pagination__next, a.pagination__next_dsktop') || node.querySelector('.product-grid-item-buy, a.pagination__item, a.pagination__next, a.pagination__next_dsktop'));
        }); })) {
          applyPriceFilter();
          preserveCostPagination();
          updateProductCount();
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  // ── Event Binding ──
  function bindEvents() {
    // Filter pill clicks → toggle dropdown
    document.querySelectorAll('.xo-filter-pill[data-filter]').forEach(function (pill) {
      pill.addEventListener('click', function (e) {
        e.stopPropagation();
        toggleDropdown(this.getAttribute('data-filter'));
      });
    });

    // Mobile filter trigger
    document.querySelectorAll('.xo-filter-pill--mobile-trigger').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        // Show all filters in a mobile sheet — for now, toggle first available
        var firstPill = document.querySelector('.xo-filter-pill[data-filter]');
        if (firstPill) toggleDropdown(firstPill.getAttribute('data-filter'));
      });
    });

    // Checkbox option clicks
    document.querySelectorAll('.xo-filter-option').forEach(function (opt) {
      opt.addEventListener('click', function (e) {
        e.stopPropagation();
        var group = this.closest('.xo-filter-dropdown').getAttribute('data-group');
        var tag = this.getAttribute('data-tag');

        // Single-select per group: toggle this one
        var groupOptions = this.closest('.xo-filter-dropdown').querySelectorAll('.xo-filter-option');
        groupOptions.forEach(function (o) {
          if (o.getAttribute('data-tag') === tag) {
            o.classList.toggle('is-selected');
          } else {
            o.classList.remove('is-selected');
          }
        });

        // Update pending selection
        if (this.classList.contains('is-selected')) {
          pendingSelections[group] = tag;

          // Auto-set shopfor when selecting a theme from a section
          if (group === 'theme') {
            var section = this.closest('.xo-theme-section');
            if (section) {
              var sectionMap = {
                'bachelorette-bride': 'shopfor-bachelorette-bride',
                'bridal-shower': 'shopfor-bridal-shower',
                'birthday': 'shopfor-birthday',
                'party': 'shopfor-party',
                'kids-party': 'shopfor-kids-party',
                'baby-shower': 'shopfor-baby-shower'
              };
              var sectionKey = section.getAttribute('data-section');
              if (sectionMap[sectionKey] && !pendingSelections['shopfor']) {
                pendingSelections['shopfor'] = sectionMap[sectionKey];
              }
            }
          }
        } else {
          delete pendingSelections[group];
        }
      });
    });

    // Color swatch clicks
    document.querySelectorAll('.xo-filter-swatch').forEach(function (swatch) {
      swatch.addEventListener('click', function (e) {
        e.stopPropagation();
        var group = 'color';
        var tag = this.getAttribute('data-tag');

        // Single-select
        var allSwatches = this.closest('.xo-filter-dropdown').querySelectorAll('.xo-filter-swatch');
        allSwatches.forEach(function (s) {
          if (s.getAttribute('data-tag') === tag) {
            s.classList.toggle('is-selected');
          } else {
            s.classList.remove('is-selected');
          }
        });

        if (this.classList.contains('is-selected')) {
          pendingSelections[group] = tag;
        } else {
          delete pendingSelections[group];
        }
      });
    });

    // Price button clicks
    document.querySelectorAll('.xo-filter-price-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var min = parseFloat(this.getAttribute('data-min'));
        var max = parseFloat(this.getAttribute('data-max'));

        // Single-select
        var allPriceBtns = this.closest('.xo-filter-dropdown').querySelectorAll('.xo-filter-price-btn');
        allPriceBtns.forEach(function (b) {
          if (b === btn) {
            b.classList.toggle('is-selected');
          } else {
            b.classList.remove('is-selected');
          }
        });

        if (this.classList.contains('is-selected')) {
          pendingSelections['price'] = { min: min, max: max };
        } else {
          delete pendingSelections['price'];
        }
      });
    });

    // Apply buttons
    document.querySelectorAll('.xo-filter-apply-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        applyFilters();
      });
    });

    // Clear buttons (within a dropdown)
    document.querySelectorAll('.xo-filter-clear-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var dropdown = this.closest('.xo-filter-dropdown');
        var group = dropdown.getAttribute('data-group');

        // Deselect all in this dropdown
        dropdown.querySelectorAll('.is-selected').forEach(function (el) {
          el.classList.remove('is-selected');
        });

        delete pendingSelections[group];
      });
    });

    // Active pill remove clicks
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('.xo-active-pill');
      if (pill) {
        var group = pill.getAttribute('data-group');
        if (group === 'price') {
          selectedPrice = null;
          delete pendingSelections['price'];
        } else {
          delete selectedFilters[group];
          delete pendingSelections[group];
        }
        navigateToFilteredUrl();
      }

      var clearAll = e.target.closest('.xo-active-clear-all');
      if (clearAll) {
        selectedFilters = {};
        selectedPrice = null;
        pendingSelections = {};
        navigateToFilteredUrl();
      }
    });

    // Close dropdowns on outside click
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.xo-filter-pill') && !e.target.closest('.xo-filter-dropdown')) {
        closeAllDropdowns();
      }
    });

    // Sort dropdown change
    document.querySelectorAll('.xo-sort-select').forEach(function (select) {
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.value);
        window.location.href = url.toString();
      });
    });
  }

  // ── Dropdown Toggle ──
  function toggleDropdown(filterName) {
    var wasOpen = false;
    var targetDropdown = null;

    // Close all dropdowns first
    document.querySelectorAll('.xo-filter-dropdown').forEach(function (dd) {
      if (dd.getAttribute('data-group') === filterName && dd.classList.contains('is-visible')) {
        wasOpen = true;
      }
      dd.classList.remove('is-visible');
    });

    document.querySelectorAll('.xo-filter-pill').forEach(function (p) {
      p.classList.remove('is-open');
    });

    var overlay = document.querySelector('.xo-filter-overlay');
    if (overlay) overlay.classList.remove('is-visible');
    var bar = document.querySelector('.xo-filter-bar');
    if (bar) bar.classList.remove('xo-dropdown-open');

    if (wasOpen) {
      pendingSelections = Object.assign({}, selectedFilters);
      if (selectedPrice) pendingSelections['price'] = selectedPrice;
      return;
    }

    // Open the target dropdown
    // Sync pending state with current state
    pendingSelections = Object.assign({}, selectedFilters);
    if (selectedPrice) pendingSelections['price'] = selectedPrice;

    // Find dropdown closest to the clicked pill (in same mobile/desktop section)
    document.querySelectorAll('.xo-filter-dropdown[data-group="' + filterName + '"]').forEach(function (dd) {
      // Show all matching dropdowns (mobile + desktop have separate copies)
      var parentSection = dd.closest('section');
      if (parentSection) {
        var isHidden = window.getComputedStyle(parentSection).display === 'none';
        if (!isHidden) {
          dd.classList.add('is-visible');
          targetDropdown = dd;
        }
      } else {
        dd.classList.add('is-visible');
        targetDropdown = dd;
      }
    });

    document.querySelectorAll('.xo-filter-pill[data-filter="' + filterName + '"]').forEach(function (p) {
      var parentSection = p.closest('section');
      if (parentSection) {
        var isHidden = window.getComputedStyle(parentSection).display === 'none';
        if (!isHidden) p.classList.add('is-open');
      } else {
        p.classList.add('is-open');
      }
    });

    if (overlay) overlay.classList.add('is-visible');
    var bar = document.querySelector('.xo-filter-bar');
    if (bar) bar.classList.add('xo-dropdown-open');
    trackFilter('filter_open', filterName);

    // Set selected state in the opened dropdown
    if (targetDropdown) {
      syncDropdownState(targetDropdown, filterName);
    }
  }

  function syncDropdownState(dropdown, group) {
    if (group === 'price') {
      dropdown.querySelectorAll('.xo-filter-price-btn').forEach(function (btn) {
        var min = parseFloat(btn.getAttribute('data-min'));
        var max = parseFloat(btn.getAttribute('data-max'));
        if (selectedPrice && selectedPrice.min === min && selectedPrice.max === max) {
          btn.classList.add('is-selected');
        } else {
          btn.classList.remove('is-selected');
        }
      });
    } else if (group === 'color') {
      dropdown.querySelectorAll('.xo-filter-swatch').forEach(function (s) {
        if (selectedFilters[group] && s.getAttribute('data-tag') === selectedFilters[group]) {
          s.classList.add('is-selected');
        } else {
          s.classList.remove('is-selected');
        }
      });
    } else {
      dropdown.querySelectorAll('.xo-filter-option').forEach(function (opt) {
        if (selectedFilters[group] && opt.getAttribute('data-tag') === selectedFilters[group]) {
          opt.classList.add('is-selected');
        } else {
          opt.classList.remove('is-selected');
        }
      });
    }
  }

  function closeAllDropdowns() {
    document.querySelectorAll('.xo-filter-dropdown').forEach(function (dd) {
      dd.classList.remove('is-visible');
    });
    document.querySelectorAll('.xo-filter-pill').forEach(function (p) {
      p.classList.remove('is-open');
    });
    var overlay = document.querySelector('.xo-filter-overlay');
    if (overlay) overlay.classList.remove('is-visible');
    var bar = document.querySelector('.xo-filter-bar');
    if (bar) bar.classList.remove('xo-dropdown-open');

    // Reset pending
    pendingSelections = Object.assign({}, selectedFilters);
    if (selectedPrice) pendingSelections['price'] = selectedPrice;
  }

  // ── Analytics ──
  function trackFilter(action, details) {
    if (typeof gtag === 'function') {
      gtag('event', action, { event_category: 'collection_filters', event_label: details });
    }
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({ event: 'xo_filter', filter_action: action, filter_details: details });
    }
    try {
      var sid = sessionStorage.getItem('xo_sid');
      if (!sid) {
        sid = Math.random().toString(36).substring(2) + Date.now().toString(36);
        sessionStorage.setItem('xo_sid', sid);
      }
      fetch('https://story-inventory.com/api/filter-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          action: action,
          details: typeof details === 'string' ? details : JSON.stringify(details),
          url: window.location.href,
          session_id: sid
        }),
        keepalive: true
      });
    } catch (e) {}
  }

  // ── Apply Filters ──
  function applyFilters() {
    // Merge pending into selected
    PREFIXES.forEach(function (prefix) {
      if (pendingSelections[prefix]) {
        selectedFilters[prefix] = pendingSelections[prefix];
      } else {
        delete selectedFilters[prefix];
      }
    });

    if (pendingSelections['price']) {
      selectedPrice = pendingSelections['price'];
    } else {
      selectedPrice = null;
    }

    var applied = [];
    PREFIXES.forEach(function (p) { if (selectedFilters[p]) applied.push(selectedFilters[p]); });
    if (selectedPrice) applied.push('price-' + selectedPrice.min + '-' + selectedPrice.max);
    trackFilter('filter_apply', applied.join(', '));

    closeAllDropdowns();
    navigateToFilteredUrl();
  }

  // ── URL Navigation ──
  function navigateToFilteredUrl() {
    var configEl = document.getElementById('xo-filter-config');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    // Build tag path
    var tags = [];
    PREFIXES.forEach(function (prefix) {
      if (selectedFilters[prefix]) {
        tags.push(selectedFilters[prefix]);
      }
    });

    var url = config.collectionUrl;
    if (tags.length) {
      url += '/' + tags.join('+');
    }

    // Add sort and price params
    var params = new URLSearchParams();
    var currentParams = new URLSearchParams(window.location.search);

    if (currentParams.get('sort_by')) {
      params.set('sort_by', currentParams.get('sort_by'));
    }

    if (selectedPrice) {
      params.set('xo_price', selectedPrice.min + '-' + selectedPrice.max);
    }

    var paramString = params.toString();
    if (paramString) {
      url += '?' + paramString;
    }

    window.location.href = url;
  }

  // ── Active Filter Pills ──
  function renderActivePills() {
    document.querySelectorAll('.xo-active-filters').forEach(function (container) {
      container.innerHTML = '';
      var hasFilters = false;

      // Tag-based active filters
      PREFIXES.forEach(function (prefix) {
        if (selectedFilters[prefix]) {
          hasFilters = true;
          // Look up original label from dropdown option to preserve special chars like &
          var label = '';
          var dropdown = document.querySelector('.xo-filter-dropdown[data-group="' + prefix + '"]');
          if (dropdown) {
            var activeOpt = dropdown.querySelector('[data-tag="' + selectedFilters[prefix] + '"]');
            if (activeOpt) {
              var labelEl = activeOpt.querySelector('.xo-filter-option__label') || activeOpt.querySelector('.xo-filter-swatch__label');
              if (labelEl) label = labelEl.textContent.trim();
            }
          }
          if (!label) {
            label = selectedFilters[prefix].replace(prefix + '-', '').replace(/-/g, ' ');
            label = titleCase(label);
          }

          var pill = document.createElement('span');
          pill.className = 'xo-active-pill';
          pill.setAttribute('data-group', prefix);
          pill.innerHTML = label + ' <span class="xo-active-pill__x">&times;</span>';
          container.appendChild(pill);
        }
      });

      // Price active filter
      if (selectedPrice) {
        hasFilters = true;
        var priceLabel = '';
        PRICE_RANGES.forEach(function (r) {
          if (r.min === selectedPrice.min && r.max === selectedPrice.max) {
            priceLabel = r.label;
          }
        });
        if (!priceLabel) priceLabel = '$' + selectedPrice.min + ' - $' + selectedPrice.max;

        var pricePill = document.createElement('span');
        pricePill.className = 'xo-active-pill';
        pricePill.setAttribute('data-group', 'price');
        pricePill.innerHTML = priceLabel + ' <span class="xo-active-pill__x">&times;</span>';
        container.appendChild(pricePill);
      }

      // Clear all link
      if (hasFilters) {
        var clearLink = document.createElement('span');
        clearLink.className = 'xo-active-clear-all';
        clearLink.textContent = 'Clear All';
        container.appendChild(clearLink);
      }


      // Update mobile trigger count
      document.querySelectorAll('.xo-filter-pill--mobile-trigger').forEach(function (trigger) {
        var count = Object.keys(selectedFilters).length + (selectedPrice ? 1 : 0);
        if (count > 0) {
          trigger.innerHTML = '<span class="xo-filter-dot"></span> Filters (' + count + ')';
        } else {
          trigger.innerHTML = '<span class="xo-filter-dot"></span> Filters';
        }
      });
    });
  }

  // ── Client-side Price Filter ──
  function preserveCostPagination() {
    document.querySelectorAll('a.pagination__item, a.pagination__next, a.pagination__next_dsktop').forEach(function (link) {
      var url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      url.searchParams.set('xo_price', selectedPrice.min + '-' + selectedPrice.max);
      link.href = url.toString();
    });
  }

  function applyPriceFilter() {
    if (!selectedPrice) return;

    document.querySelectorAll('.product-grid-item-buy').forEach(function (card) {
      var priceAttr = card.getAttribute('data-price');
      if (!priceAttr) return;

      var price = parseFloat(priceAttr);
      if (price >= selectedPrice.min && (price < selectedPrice.max || (wholesale && selectedPrice.max === 999999))) {
        card.style.display = '';
      } else {
        card.style.display = 'none';
      }
    });
  }

  function updateProductCount() {
    var visible = document.querySelectorAll('.product-grid-item-buy:not([style*="display: none"])').length;
    document.querySelectorAll('.xo-filter-count').forEach(function (el) {
      var configEl = document.getElementById('xo-filter-config');
      if (!configEl) return;
      var config = JSON.parse(configEl.textContent);

      if (selectedPrice || Object.keys(selectedFilters).length) {
        el.innerHTML = '<strong>' + visible + '</strong> of <strong>' + config.totalProducts + '</strong> products';
      } else {
        el.innerHTML = '<strong>' + config.totalProducts + '</strong> products';
      }
    });
  }

  // ── Helpers ──
  function slugify(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  function titleCase(str) {
    return str.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // ── Start ──
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
